import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Spinner,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axiosInstance from "../../../helpers/axiosInstance";

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    status: "",
    paymentStatus: "",
    paymentMethod: "",
    shippingAddress: {
      street: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    },
    instructions: "",
  });
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setFullscreenImageUrl(null);
    };
    if (fullscreenImageUrl) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [fullscreenImageUrl]);

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  useEffect(() => {
    if (orderData) {
      setFormData({
        status: orderData.status,
        paymentStatus: orderData.paymentStatus,
        paymentMethod: orderData.paymentMethod,
        shippingAddress: { ...orderData.shippingAddress },
        instructions: orderData.instructions || "",
      });
    }
  }, [orderData]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/v1/admin/orders/${id}`);
      setOrderData(response.data.order);
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setUpdateLoading(true);
      const response = await axiosInstance.put(
        `/api/v1/admin/orders/${id}`,
        formData
      );
      setEditMode(false);
      fetchOrderDetails()
    } catch (error) {
      console.error("Error updating order:", error);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/api/v1/admin/orders/${id}`);
      navigate("/ecommerce-orders");
    } catch (error) {
      console.error("Error deleting order:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return "success";
      case "outForDelivery":
      case "printed":
      case "accepted":
        return "info";
      case "cancelled":
      case "rejected":
        return "danger";
      case "pending":
        return "warning";
      default:
        return "secondary";
    }
  };



  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Order Details"
          breadcrumbItems={[
            { title: "Ecommerce", link: "#" },
            { title: "Orders", link: "/ecommerce-orders" },
            { title: "Order Details", link: "#" },
          ]}
        />

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="card-title mb-1">Order #{orderData?.id}</h4>
                    <p className="text-muted mb-0">
                      Placed on {formatDate(orderData?.orderedAt)}
                    </p>
                  </div>
                  <div className="d-flex gap-2">
                    {editMode ? (
                      <>
                        <Button
                          color="success"
                          onClick={handleUpdate}
                          disabled={updateLoading}
                        >
                          {updateLoading ? (
                            <Spinner size="sm" />
                          ) : (
                            "Save Changes"
                          )}
                        </Button>
                        <Button
                          color="light"
                          onClick={() => setEditMode(false)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          color="primary"
                          onClick={() => setEditMode(true)}
                        >
                          Edit Order
                        </Button>
                        <Button
                          color="danger"
                          onClick={() => setDeleteModal(true)}
                        >
                          Delete Order
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <Row>
                  <Col md={4}>
                    <Card className="bg-light">
                      <CardBody>
                        <h5 className="card-title mb-3">Customer Details</h5>
                        <div>
                          <h6 className="mb-1">{orderData?.user?.name}</h6>
                          <p className="mb-1">{orderData?.user?.email}</p>
                          <p className="mb-0">ID: #{orderData?.user?.id}</p>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md={4}>
                    <Card className="bg-light">
                      <CardBody>
                        <h5 className="card-title mb-3">Shipping Address</h5>
                        {editMode ? (
                          <Form>
                            <FormGroup>
                              <Input
                                type="text"
                                value={formData.shippingAddress.street}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    shippingAddress: {
                                      ...formData.shippingAddress,
                                      street: e.target.value,
                                    },
                                  })
                                }
                                placeholder="Street"
                              />
                            </FormGroup>
                            <Row>
                              <Col md={6}>
                                <FormGroup>
                                  <Input
                                    type="text"
                                    value={formData.shippingAddress.city}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        shippingAddress: {
                                          ...formData.shippingAddress,
                                          city: e.target.value,
                                        },
                                      })
                                    }
                                    placeholder="City"
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Input
                                    type="text"
                                    value={formData.shippingAddress.state}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        shippingAddress: {
                                          ...formData.shippingAddress,
                                          state: e.target.value,
                                        },
                                      })
                                    }
                                    placeholder="State"
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col md={6}>
                                <FormGroup>
                                  <Input
                                    type="text"
                                    value={formData.shippingAddress.country}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        shippingAddress: {
                                          ...formData.shippingAddress,
                                          country: e.target.value,
                                        },
                                      })
                                    }
                                    placeholder="Country"
                                  />
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Input
                                    type="text"
                                    value={formData?.shippingAddress?.postalCode}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        shippingAddress: {
                                          ...formData.shippingAddress,
                                          postalCode: e.target.value,
                                        },
                                      })
                                    }
                                    placeholder="Postal Code"
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                          </Form>
                        ) : (
                          <div>

                            <p className="mb-1">
                              {orderData?.shippingAddress?.street}
                            </p>
                            <p className="mb-1">
                              {orderData?.shippingAddress?.city},{" "}
                              {orderData?.shippingAddress?.state}
                            </p>
                            <p className="mb-1">
                              {orderData?.shippingAddress?.country} -{" "}
                              {orderData?.shippingAddress?.postalCode}
                            </p>
                            <p className="mb-1">
                              {orderData?.shippingAddress?.email}
                            </p>
                            <p className="mb-1">
                              {orderData?.shippingAddress?.phone}
                            </p>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md={4}>
                    <Card className="bg-light">
                      <CardBody>
                        <h5 className="card-title mb-3">Order Status</h5>
                        {editMode ? (
                          <Form>
                            <FormGroup>
                              <Label>Order Status</Label>
                              <Input
                                type="select"
                                value={formData.status}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    status: e.target.value,
                                  })
                                }
                              >
                                <option value="pending">Pending</option>
                                <option value="accepted">Accepted</option>
                                <option value="printed">Printed</option>
                                <option value="outForDelivery">
                                  Dispatched
                                </option>
                                <option value="delivered">Delivered</option>
                                <option value="rejected">Rejected</option>
                                <option value="cancelled">Cancelled</option>
                              </Input>
                            </FormGroup>
                            <FormGroup>
                              <Label>Payment Status</Label>
                              <Input
                                type="select"
                                value={formData.paymentStatus}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    paymentStatus: e.target.value,
                                  })
                                }
                              >
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                                <option value="failed">Failed</option>
                                <option value="refunded">Refunded</option>
                              </Input>
                            </FormGroup>
                            <FormGroup>
                              <Label>Payment Method</Label>
                              <Input
                                type="select"
                                value={formData.paymentMethod}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    paymentMethod: e.target.value,
                                  })
                                }
                              >
                                <option value="online">Online</option>
                                <option value="cod">Cash on Delivery</option>
                              </Input>
                            </FormGroup>
                          </Form>
                        ) : (
                          <div>
                            <div className="mb-3">
                              <Badge
                                color={getStatusColor(orderData?.status)}
                                className="me-1"
                              >
                                Order: {orderData?.status === "outForDelivery" ? "Dispatched" : orderData?.status}
                              </Badge>
                            </div>
                            <div>
                              <Badge
                                color={getStatusColor(orderData?.paymentStatus)}
                                className="me-1"
                              >
                                Payment: {orderData?.paymentStatus}
                              </Badge>
                              <small className="text-muted d-block mt-1">
                                via {orderData?.paymentMethod}
                              </small>
                            </div>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                  {orderData?.trackingId &&
                    <Col md={4}>
                      <Card className="bg-light">
                        <CardBody>
                          <h5 className="card-title mb-3">AWB Tracking</h5>
                          <div>
                            <h6 className="mb-1">Tracking ID: {orderData?.trackingId}</h6>  
                          </div>
                        </CardBody>
                      </Card>
                    </Col>}
                  {orderData?.giftPacking?.isGiftPack &&
                    <Col md={4}>
                      <Card className="bg-light">
                        <CardBody>
                          <h5 className="card-title mb-3">Gift Packing</h5>
                          <div>
                            <h6 className="mb-1">INCLUDES GIFT WRAP / PACKING MATERIAL</h6>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>}
                  {orderData?.gstNumber &&
                    <Col md={4}>
                      <Card className="bg-light">
                        <CardBody>
                          <div>
                            <h6 className="mb-1">GST Number: {orderData?.gstNumber}</h6>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>}

                    <Col md={4}>
                      <Card className="bg-light">
                        <CardBody>
                          <h5 className="card-title mb-3">
                            Instructions
                            {!editMode && (
                              <i 
                                className="dripicons-pencil ms-2" 
                                style={{ 
                                  fontSize: "14px", 
                                  cursor: "pointer" 
                                }}
                                onClick={() => setEditMode(true)}
                                title="Edit Instructions"
                              ></i>
                            )}
                          </h5>
                          {editMode ? (
                            <FormGroup>
                              <Input
                                type="textarea"
                                rows="3"
                                value={formData.instructions || ""}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    instructions: e.target.value,
                                  })
                                }
                                placeholder="Add delivery instructions"
                              />
                            </FormGroup>
                          ) : (
                            <div>
                              <h6 className="mb-1">{orderData?.instructions}</h6>
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                </Row>

                <div className="mt-4">
                  <h5 className="card-title mb-3">Order Items</h5>
                  <div className="table-responsive">
                    <Table className="table-centered mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>Price</th>
                          <th>Discount</th>
                          <th>Quantity</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderData?.items?.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                {item.product.image && (
                                  <img
                                    src={item.product.image.startsWith("https") ? item.product.image : `${process.env.REACT_APP_BASEURL}${item.product.image}`}
                                    alt={item.product.name}
                                    className="rounded me-3"
                                    style={{
                                      width: "48px",
                                      height: "48px",
                                      objectFit: "cover",
                                      cursor: "zoom-in",
                                    }}
                                    onClick={() =>
                                      setFullscreenImageUrl(
                                        item.product.image
                                          ? item.product.image.startsWith("https")
                                            ? item.product.image
                                            : `${process.env.REACT_APP_BASEURL}${item.product.image}`
                                          : ""
                                      )
                                    }
                                  />
                                )}
                                <div>
                                  <h6 className="mb-1">{item.product.name}</h6>
                                  <div className="d-flex align-items-center">
                                    <small className="text-muted">#{item.product.id}</small>
                                    {item.colors && (
                                      <div className="ms-2 d-flex align-items-center">
                                        <small className="text-muted me-2">Color:</small>
                                        <div
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                            backgroundColor: item.colors,
                                            borderRadius: "50%",
                                            border: "2px solid #fff",
                                            boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
                                            cursor: "pointer",
                                            transition: "transform 0.2s",
                                          }}
                                          title={item.colors}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "scale(1.1)";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "scale(1)";
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>{formatCurrency(item.product.price)}</td>
                            <td>{item.product.discount}%</td>
                            <td>{item.quantity}</td>
                            <td className="text-end">
                              {formatCurrency(
                                (item.product.price -
                                  (item.product.price * item.product.discount / 100)) *
                                item.quantity
                              )}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan="4" className="text-end">
                            <strong>Delivery Charge</strong>
                          </td>
                          <td className="text-end">
                            {formatCurrency((
                              orderData?.deliveryCharge)
                            )}
                          </td>
                        </tr>

                        {orderData?.paymentMethod === "cod" &&
                          <tr>
                            <td colSpan="4" className="text-end">
                              <strong>Cash on Delivery Charge</strong>
                            </td>
                            <td className="text-end">
                              {formatCurrency(
                                50
                              )}
                            </td>
                          </tr>}
                          {orderData?.couponDiscount&&
                          <tr>
                            <td colSpan="4" className="text-end">
                              <strong>Coupon Discount</strong>
                            </td>
                            <td className="text-end">
                              {formatCurrency(
                                orderData?.couponDiscount
                              )}
                            </td>
                          </tr>}
                        <tr className="bg-light">
                          <td colSpan="4" className="text-end">
                            <strong>Total Amount</strong>
                          </td>
                          <td className="text-end">
                            <strong>
                              {formatCurrency(
                                orderData?.totalAmount
                              )}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
          <ModalHeader toggle={() => setDeleteModal(false)}>
            Confirm Delete
          </ModalHeader>
          <ModalBody>
            Are you sure you want to delete this order? This action cannot be
            undone.
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setDeleteModal(false)}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDelete}>
              Delete Order
            </Button>
          </ModalFooter>
        </Modal>
        {fullscreenImageUrl && (
          <div
            role="dialog"
            aria-modal="true"
            className="image-fullscreen-overlay"
            onClick={() => setFullscreenImageUrl(null)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
              padding: "24px",
            }}
          >
            <img
              src={fullscreenImageUrl}
              alt="Product"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
              }}
            />
            <button
              type="button"
              aria-label="Close"
              onClick={() => setFullscreenImageUrl(null)}
              style={{
                position: "fixed",
                top: 12,
                right: 12,
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                border: "none",
                borderRadius: "999px",
                width: 40,
                height: 40,
                cursor: "pointer",
                fontSize: 24,
                lineHeight: "40px",
                textAlign: "center",
              }}
            >
              ×
            </button>
          </div>
        )}
      </Container>
    </div>
  );
};

export default OrderDetails;
